import { ReactNode } from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';

// Types

export interface TabOption<T extends string = string> {
	value: T;
	label: string;
	icon?: ReactNode;
}

export interface TabSelectorProps<T extends string = string> {
	value: T;
	onChange: (value: T) => void;
	options: TabOption<T>[];
}

// Component

export const TabSelector = <T extends string = string>({
	value,
	onChange,
	options,
}: TabSelectorProps<T>) => {
	return (
		<ToggleButtonGroup
			value={value}
			exclusive
			onChange={(_, newValue) => newValue && onChange(newValue as T)}
			size="small"
			sx={{
				bgcolor: 'grey.100',
				borderRadius: 25,
				p: 0.5,
				'& .MuiToggleButtonGroup-grouped': {
					border: 'none',
					borderRadius: '20px !important',
					color: 'text.secondary',
					fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
					fontWeight: 500,
					'&.Mui-selected': {
						bgcolor: 'background.paper',
						boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
						color: 'text.primary',
						fontWeight: 600,
						'&:hover': {
							bgcolor: 'background.paper',
						},
					},
					'&:hover': {
						bgcolor: 'transparent',
					},
				},
			}}>
			{options.map((option) => (
				<ToggleButton
					key={option.value}
					value={option.value}
					sx={{
						px: { xs: 1.5, sm: 2 },
						py: 0.75,
						textTransform: 'none',
					}}>
					{option.icon}
					{option.label}
				</ToggleButton>
			))}
		</ToggleButtonGroup>
	);
};
